import { Vector3 as BABYLON_Vector3 } from '@inter-ikea-betrakta-babylonjs/core/Maths/math.vector.js';
import { icolor_to_col4, vector_3 } from "../../../../babylon/utility/Conversion.js";
import { DebugVisualizer } from "../../../../babylon/debug/visualizers/DebugVisualizer.js";
import { IColor, LineType } from "../../../../api.js";
const COLORS = {
    GREEN: new IColor(0, 1, 0),
    BLUE: new IColor(0, 0, 1),
    RED: new IColor(1, 0, 0)
};
export class OrthographicCameraVisualizer extends DebugVisualizer {
    constructor(_camera, viewer) {
        super(viewer);
        this._camera = _camera;
        this.show_pan_limits = true;
        this.update();
    }
    update() {
        // Remove all existing lines
        this.remove_lines(0);
        let index = 0;
        index = this.draw_camera_position(index);
        index = this.draw_view_frame_rectangle(index);
        if (this.show_pan_limits) {
            index = this.draw_pan_limits(index);
            if (this._camera.viewer.get_active_camera() === this._camera) {
                this.draw_active_pan_limits(index);
            }
        }
    }
    draw_camera_position(index) {
        const color = icolor_to_col4(COLORS.GREEN);
        const thickness = 0.02;
        const camera_pos = vector_3(this._camera.position);
        // Draw a small cross at the camera position
        const size = 0.1;
        const up = BABYLON_Vector3.Up().scale(size / 2);
        this.draw_line(index++, camera_pos.subtract(up), camera_pos.add(up), color, thickness);
        const right = BABYLON_Vector3.Right().scale(size / 2);
        this.draw_line(index++, camera_pos.subtract(right), camera_pos.add(right), color, thickness);
        const forward = BABYLON_Vector3.Forward().scale(size / 2);
        this.draw_line(index++, camera_pos.subtract(forward), camera_pos.add(forward), color, thickness);
        return index;
    }
    draw_view_frame_rectangle(index) {
        const color = icolor_to_col4(COLORS.GREEN);
        const thickness = 0.02;
        const camera_pos = vector_3(this._camera.position);
        const up = vector_3(this._camera.up).normalize();
        const right = vector_3(this._camera.right).normalize();
        const left = this._camera.projection.left;
        const right_proj = this._camera.projection.right;
        const top = this._camera.projection.top;
        const bottom = this._camera.projection.bottom;
        const center = camera_pos;
        // Width and height of the view frame rectangle
        const width = right_proj - left;
        const height = top - bottom;
        // Four corners of the rectangle
        const top_left = center.add(up.scale(height / 2)).add(right.scale(-width / 2));
        const top_right = center.add(up.scale(height / 2)).add(right.scale(width / 2));
        const bottom_left = center.add(up.scale(-height / 2)).add(right.scale(-width / 2));
        const bottom_right = center.add(up.scale(-height / 2)).add(right.scale(width / 2));
        // Draw view frame rectangle
        this.draw_line(index++, top_left, top_right, color, thickness);
        this.draw_line(index++, top_right, bottom_right, color, thickness);
        this.draw_line(index++, bottom_right, bottom_left, color, thickness);
        this.draw_line(index++, bottom_left, top_left, color, thickness);
        const is_active = this._camera.viewer.get_active_camera() === this._camera;
        if (!is_active) {
            this.draw_line(index++, camera_pos, top_left, color, thickness);
            this.draw_line(index++, camera_pos, top_right, color, thickness);
            this.draw_line(index++, camera_pos, bottom_left, color, thickness);
            this.draw_line(index++, camera_pos, bottom_right, color, thickness);
        }
        return index;
    }
    draw_active_pan_limits(index) {
        const color = icolor_to_col4(COLORS.BLUE);
        const thickness = 0.01;
        const max = this._camera['_pan_max'];
        const min = this._camera['_pan_min'];
        // Do nothing if we have infinitly large pan limits, ex. unset
        if (!is_finite_vector(max) || !is_finite_vector(min))
            return index;
        const up = vector_3(this._camera.up).normalize();
        const right = vector_3(this._camera.right).normalize();
        // Four corners of the rectangle
        const top_left = up.scale(max.y).add(right.scale(min.x));
        const top_right = up.scale(max.y).add(right.scale(max.x));
        const bottom_left = up.scale(min.y).add(right.scale(min.x));
        const bottom_right = up.scale(min.y).add(right.scale(max.x));
        // Draw view frame rectangle
        this.draw_line(index++, top_left, top_right, color, thickness, LineType.DASHED);
        this.draw_line(index++, top_right, bottom_right, color, thickness, LineType.DASHED);
        this.draw_line(index++, bottom_right, bottom_left, color, thickness, LineType.DASHED);
        this.draw_line(index++, bottom_left, top_left, color, thickness, LineType.DASHED);
        return index;
    }
    draw_pan_limits(index) {
        const color = icolor_to_col4(COLORS.BLUE);
        const thickness = 0.01;
        if (!('set_pan_limits' in this._camera))
            return index;
        const max = this._camera['_pan_max'];
        const min = this._camera['_pan_min'];
        // Do nothing if we have infinitly large pan limits, ex. unset
        if (!is_finite_vector(max) || !is_finite_vector(min))
            return index;
        const camera_pos = vector_3(this._camera.position);
        const up = vector_3(this._camera.up).normalize();
        const right = vector_3(this._camera.right).normalize();
        const left = this._camera.projection.left;
        const right_proj = this._camera.projection.right;
        const top = this._camera.projection.top;
        const bottom = this._camera.projection.bottom;
        const center = camera_pos;
        // Width and height of the view frame rectangle
        const width = right_proj - left;
        const height = top - bottom;
        // Four corners of the rectangle
        const top_left = center.add(up.scale(height / 2)).add(right.scale(-width / 2)).add(up.scale(max.y)).add(right.scale(min.x));
        const top_right = center.add(up.scale(height / 2)).add(right.scale(width / 2)).add(up.scale(max.y)).add(right.scale(max.x));
        const bottom_left = center.add(up.scale(-height / 2)).add(right.scale(-width / 2)).add(up.scale(min.y)).add(right.scale(min.x));
        const bottom_right = center.add(up.scale(-height / 2)).add(right.scale(width / 2)).add(up.scale(min.y)).add(right.scale(max.x));
        // Draw view frame rectangle
        this.draw_line(index++, top_left, top_right, color, thickness, LineType.DASHED);
        this.draw_line(index++, top_right, bottom_right, color, thickness, LineType.DASHED);
        this.draw_line(index++, bottom_right, bottom_left, color, thickness, LineType.DASHED);
        this.draw_line(index++, bottom_left, top_left, color, thickness, LineType.DASHED);
        return index;
    }
}
function is_finite(...values) {
    return values.every(v => isFinite(v));
}
function is_finite_vector({ x, y }) {
    return is_finite(x, y);
}
//# sourceMappingURL=OrthographicCameraVisualizer.js.map